import {
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { Cargo, FreightService, FreightServiceAddOn, WeightUnit } from '@pelicargo/types';
import { buildCargoString, Show } from '@pelicargo/ui';
import {
  ensureDimensionUnit,
  getHandlingAndPacking,
  getPrettyCargoType,
  getPrettyCommodity,
  getPrettyDimensionUnit,
  getPrettyFreightServiceAddOn,
  kgToLb,
  prettyBoolean,
  prettyDate,
  prettyNumber,
} from '@pelicargo/utils';
import { Fragment, useMemo } from 'react';

import { TrpcOutputs } from '../config/trpc';
import { CopyButton } from './CopyButton';
import { LabeledValue } from './LabeledValue';

type Quote = TrpcOutputs['getFinalQuote']['quote'];
type Request = TrpcOutputs['getFinalQuote']['quote']['request'];

type Props = {
  request: Request;
  quote: Quote;
};

export const RequestDetailsCard = ({ request, quote }: Props) => {
  const { isOpen: showAllCargo, onToggle: toggleShowAllCargo } = useDisclosure({ defaultIsOpen: false });

  const dimensionUnit = request?.original_dimension_unit;
  const weightUnit = request?.original_weight_unit;
  const isInLbs = weightUnit === WeightUnit.lb;

  const inlandQuote = request?.booked_quote_variant?.inland_service_quote_variant?.quote;

  const isFCA = useMemo(() => inlandQuote?.freight_service === FreightService.FCA, [inlandQuote?.freight_service]);
  const isEXW = useMemo(() => inlandQuote?.freight_service === FreightService.EXW, [inlandQuote?.freight_service]);
  const isInland = isFCA || isEXW;

  const renderCargoItem = (item: Cargo) => {
    const length = ensureDimensionUnit(item?.length, dimensionUnit);
    const width = ensureDimensionUnit(item?.width, dimensionUnit);
    const height = ensureDimensionUnit(item?.height, dimensionUnit);

    return (
      <Fragment key={item?.id}>
        <GridItem>
          <Text textStyle="bodySmallSemibold">{item?.quantity} pcs</Text>
        </GridItem>
        <GridItem>
          <Text textStyle="bodySmallSemibold">
            {prettyNumber(length)}x{prettyNumber(width)}x{prettyNumber(height)} {getPrettyDimensionUnit(dimensionUnit)}
          </Text>
        </GridItem>
        <GridItem>
          <Show if={isInLbs}>
            <Tooltip
              label={`This weight has been converted from ${prettyNumber(kgToLb(item?.weight))} LBs to ${prettyNumber(item?.weight)} KGs`}
            >
              <Text textStyle="bodySmallSemibold">{prettyNumber(item?.weight)} kg</Text>
            </Tooltip>
          </Show>
          <Show if={!isInLbs}>
            <Text textStyle="bodySmallSemibold">{prettyNumber(item?.weight)} kg</Text>
          </Show>
        </GridItem>
        <GridItem>
          <Text textStyle="bodySmallSemibold">{getHandlingAndPacking(item)}</Text>
        </GridItem>
      </Fragment>
    );
  };

  return (
    <Card h="auto" px="4" py="3">
      <CardBody p="0">
        <Stack divider={<Divider />}>
          <Stack>
            <Heading variant="h5">Request Details</Heading>
            <Heading variant="h6">Cargo</Heading>
            <Stack direction="row" justify="flex-start" wrap="wrap" spacing="6">
              <LabeledValue label="Cargo Type" value={getPrettyCargoType(request?.cargo_type)} />
              <LabeledValue label="Commodity" value={getPrettyCommodity(request?.commodity)} />
              <LabeledValue
                label="Weight"
                value={`Gross ${prettyNumber(request?.gross_weight)} kg | Vol ${prettyNumber(request?.volumetric_weight)} kg`}
              />
              <LabeledValue label="Known Shipper" value={prettyBoolean(request?.is_known_shipper)} />
              <LabeledValue label="Tender" value={prettyDate(quote?.tender_date)} />
            </Stack>
            <Stack role="group">
              <Flex justifyContent="space-between" alignItems="start">
                <LabeledValue label="Cargo" overflowX="auto">
                  <Stack>
                    <Grid gridTemplateColumns="repeat(4, auto)" columnGap={3} rowGap={2}>
                      {request?.cargo?.slice(0, showAllCargo ? request?.cargo?.length : 3)?.map(renderCargoItem)}
                    </Grid>
                    <Show if={request?.cargo?.length > 3}>
                      <Button variant="outline" onClick={toggleShowAllCargo} alignSelf="flex-start">
                        {showAllCargo ? 'View less' : 'View more'}
                      </Button>
                    </Show>
                  </Stack>
                </LabeledValue>
                <CopyButton text={buildCargoString(request?.cargo, dimensionUnit, weightUnit)} />
              </Flex>
            </Stack>
          </Stack>
          {isInland && (
            <Stack>
              <Heading variant="h6">Paperwork</Heading>
              {Object.values(FreightServiceAddOn)?.map((addOn) => (
                <LabeledValue
                  key={addOn}
                  label={getPrettyFreightServiceAddOn(addOn)}
                  value={request?.freight_service_add_ons?.includes(addOn) ? 'Yes' : 'No'}
                />
              ))}
              <LabeledValue label="HAWB Required" value={request?.is_consolidated ? 'Yes' : 'No'} />
              <LabeledValue label="AWB Instructions" value={inlandQuote?.awb_instructions} defaultValue="-" />
              <LabeledValue label="" value={quote?.additional_information} />
            </Stack>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
};
